import { forwardRef, useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { NoMessageValues, MessageValue } from 'react-intl';

import type { FilterSettingsMobileButtonV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsMobileButtonV2FragmentFragmentDoc';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';

import { Button } from '@packages/shared/src/ui/Button/Button';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { Badge } from '@packages/shared/src/ui/Badge/Badge';

import { SelectedFilterValueListV2 } from '../SelectedFilterValueListV2/SelectedFilterValueListV2';

/* GraphQL */ `
  fragment FilterSettingsMobileButtonV2Fragment on SearchFilterableAndSortableResult {
    activeFilters {
      filterId
      activeValues {
        id
        name
      }
    }
    ...SelectedFilterValueListV2Fragment
  }
`;

export type FilterSettingsMobileButtonV2Props = {
  maskedData: FragmentType<typeof FilterSettingsMobileButtonV2FragmentFragmentDoc>;
  children: ReactNode;
  onClick?: () => void;
};

const messages = defineMessages<{
  readonly noFiltersSelected: NoMessageValues;
  readonly selectedFiltersCount: { readonly count: MessageValue };
  readonly toggleSelectedFilters: NoMessageValues;
}>({
  noFiltersSelected: {
    id: 'filtersV2.mobile.button.noFiltersSelected.text',
    defaultMessage: 'Keine Filter ausgewählt',
  },
  selectedFiltersCount: {
    id: 'filtersV2.mobile.button.selectedFiltersCount.text',
    defaultMessage: '{count} Filter ausgewählt',
  },
  toggleSelectedFilters: {
    id: 'filtersV2.mobile.button.toggleSelectedFilters.label',
    defaultMessage: 'Ausgewählte Filter ein- oder ausblenden',
  },
});

export const FilterSettingsMobileButtonV2 = forwardRef<
  HTMLButtonElement,
  FilterSettingsMobileButtonV2Props
>(({ maskedData, children, onClick }, ref) => {
  const { formatMessage } = useIntl();

  const data = unmask<typeof FilterSettingsMobileButtonV2FragmentFragmentDoc>(maskedData);

  const [isExpanded, setIsExpanded] = useState(false);

  const activeFiltersValuesCount = data.activeFilters
    ? data.activeFilters.reduce((count, filter) => {
        const activeValuesCount =
          'activeValues' in filter && Array.isArray(filter.activeValues)
            ? filter.activeValues.length
            : 0;
        const activeRangeCount =
          'activeRange' in filter && typeof filter.activeRange === 'object' && filter.activeRange
            ? 1
            : 0;

        return count + activeValuesCount + activeRangeCount;
      }, 0)
    : 0;

  return (
    <div className="bg-color-grey-light -mx-1 px-2 py-1">
      <div className="bg-color-background-base rounded-md">
        <div className="flex w-full">
          <Button
            ref={ref}
            className={`bg-color-background-base hover:bg-color-background-base flex h-[2.75rem] flex-1 items-center justify-start gap-1 rounded-s-md border-none px-1 no-underline sm:h-[2.25rem] ${activeFiltersValuesCount > 0 ? 'rounded-e-none' : 'rounded-e-md'}`}
            onClick={onClick}
          >
            <span className="icon-emp-filter min-h-4 min-w-4" />

            <ConditionalWrapper
              condition={activeFiltersValuesCount > 0}
              wrapper={(ch) => (
                <Badge
                  badgeContent={' '}
                  badgeContentClassName="min-w-1 max-h-1 -right-0.5 -mt-0.5 px-0 py-0"
                >
                  {ch}
                </Badge>
              )}
            >
              <span className="text-left underline sm:whitespace-nowrap">{children}</span>
            </ConditionalWrapper>

            <span
              className={`text-size-body-xs ml-auto text-right ${activeFiltersValuesCount === 0 ? 'mr-1' : ''}`}
            >
              {activeFiltersValuesCount && activeFiltersValuesCount > 0
                ? formatMessage(messages.selectedFiltersCount, {
                    count: activeFiltersValuesCount,
                  })
                : formatMessage(messages.noFiltersSelected)}
            </span>
          </Button>

          {activeFiltersValuesCount > 0 && (
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={formatMessage(messages.toggleSelectedFilters)}
              aria-expanded={isExpanded}
              className="bg-color-background-base hover:bg-color-background-base flex h-[2.75rem] rounded-s-none rounded-e-md border-none pr-1 pl-0 sm:h-[2.25rem]"
            >
              {isExpanded ? (
                <span className="icon-emp-arrow-medium-up size-4" />
              ) : (
                <span className="icon-emp-arrow-medium-down size-4" />
              )}
            </Button>
          )}
        </div>

        {isExpanded && activeFiltersValuesCount > 0 && (
          <div className="border-color-grey border-t px-2 py-1">
            <SelectedFilterValueListV2 maskedData={data} showColors />
          </div>
        )}
      </div>
    </div>
  );
});
